import { AccessData, TableRes } from '../../../shared/types';
import { CONFIG_SCHEMA } from '../../../shared/services/config';
import { t } from '../../../i18next';
import { getApiBaseUrl } from '../../../shared/utils/apiBase';

export type RowsProps = {
  add: any[];
  edit: any[];
  editRowOriginal: Record<string, any>;
};

export type ConfirmBeforeCommitResult = {
  shouldOpenConfirmation: boolean;
  confirmationProps?: {
    title?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    children?: React.ReactNode;
  };
};

async function fetchPspInputRoles(): Promise<any[]> {
  const apiBase = getApiBaseUrl();
  const response = await fetch(`${apiBase}/api/pspInputRoles`);
  const json = await response.json();
  return json?.[`${CONFIG_SCHEMA}_mbd_roles`] || [];
}

type RequiredAttributes = 'psp_c_a_element_cmm' | 'psp_c_a_element_femi' | 'psp_c_a_element_ncp';
type AccessEntry = { accessLevel: 'U' | 'S' | string };

async function userHasPspAccess(
  roleIds: number[],
  accessDetails: AccessData & Partial<Record<RequiredAttributes | string, AccessEntry>>,
  requiredAttributes: RequiredAttributes[],
): Promise<boolean> {
  const accessArray = requiredAttributes.map((attr) => {
    const result = accessDetails?.[`${attr}`]?.accessLevel !== 'U';
    return !!result;
  });
  if (accessArray.some((access) => access)) return false;
  const allowedRoles = ['ME', 'AV'];
  const allowedRolesIds = (await fetchPspInputRoles())
    .filter((role: { role_name: string }) => allowedRoles.includes(role.role_name))
    .map((role: { id: number }) => role.id);
  return roleIds.some((roleId) => allowedRolesIds.includes(roleId));
}

export async function PSPInputConfirmBeforeCommit(
  rowsProps: RowsProps,
  tableDetails: TableRes,
  accessData: {
    accessDetails: AccessData & Partial<Record<RequiredAttributes | string, AccessEntry>>;
    roleIds: number[];
  },
): Promise<ConfirmBeforeCommitResult> {
  const screenId = tableDetails?.id;
  const addedRows = Array.isArray(rowsProps?.add) ? rowsProps.add : [];
  const editedRows = Array.isArray(rowsProps?.edit) ? rowsProps.edit : [];
  if ((!addedRows.length && !editedRows.length) || !screenId) {
    return { shouldOpenConfirmation: false };
  }
  // If user is not ME/AV OR does not have edit access to PSP (C-A Element)
  const requiredAttributes: RequiredAttributes[] = [
    'psp_c_a_element_cmm',
    'psp_c_a_element_femi',
    'psp_c_a_element_ncp',
  ];

  try {
    if (
      !(await userHasPspAccess(accessData.roleIds, accessData.accessDetails, requiredAttributes))
    ) {
      return { shouldOpenConfirmation: false };
    }

    const hasAllMissingInAdd = addedRows.some((row) =>
      requiredAttributes.every((attr) => !row?.[`${attr}`]),
    );

    const hasAllMissingInEdit = editedRows.some((row) =>
      requiredAttributes.every((attr) => !row?.[`${attr}`] || row?.[`${attr}`] == ''),
    );

    const shouldOpenConfirmation = hasAllMissingInAdd || hasAllMissingInEdit;
    return {
      shouldOpenConfirmation,
      confirmationProps: shouldOpenConfirmation
        ? {
            title: 'npiSteering.psp_input_missing_title',
            confirmLabel: 'save_anyway',
            cancelLabel: 'cancel',
            children: <>{t('npiSteering.psp_input_missing_message')}</>,
          }
        : undefined,
    };
  } catch {
    return { shouldOpenConfirmation: false };
  }
}
